<template>
  <div class="activity-tab task-details-tabbed__scroll-body" data-testid="activity-tab">
    <div class="activity-tab__card" data-testid="activity-tab__card">
      <header class="activity-tab__header" data-testid="activity-tab__header">
        <span class="activity-tab__title" data-testid="activity-tab__title">Activity</span>
        <p class="activity-tab__subtitle" data-testid="activity-tab__subtitle">
          A complete history of changes and activity on this task.
        </p>
      </header>

      <div v-if="loading" class="activity-tab__loading" data-testid="activity-tab__loading">
        <v-progress-circular indeterminate color="primary" size="32" />
      </div>

      <div v-else-if="!groupedEntries.length" class="activity-tab__empty" data-testid="activity-tab__empty">
        No activity recorded yet.
      </div>

      <div v-else class="activity-tab__timeline" data-testid="activity-tab__timeline">
        <section
          v-for="(group, groupIndex) in groupedEntries"
          :key="group.dateKey"
          class="activity-tab__date-group"
          :data-testid="`activity-tab__date-group-${groupIndex}`"
        >
          <div class="activity-tab__date-label" :data-testid="`activity-tab__date-${groupIndex}`">
            {{ group.dateLabel }}
          </div>

          <div class="activity-tab__entries">
            <div
              v-if="group.items.length > 1"
              class="activity-tab__entries-line"
              aria-hidden="true"
            />

            <div
              v-for="(entry, entryIndex) in group.items"
              :key="`${group.dateKey}-${entryIndex}`"
              class="activity-tab__row"
              :data-testid="`activity-tab__entry-${groupIndex}-${entryIndex}`"
            >
              <div class="activity-tab__rail" aria-hidden="true">
                <span class="activity-tab__avatar">{{ entry.initials }}</span>
              </div>

              <div class="activity-tab__content">
                <p class="activity-tab__action" :data-testid="`activity-tab__action-${groupIndex}-${entryIndex}`">
                  {{ entry.actionLine }}
                </p>
                <p class="activity-tab__timestamp" :data-testid="`activity-tab__timestamp-${groupIndex}-${entryIndex}`">
                  {{ entry.timestamp }}
                </p>
                <div
                  v-if="entry.detail"
                  class="activity-tab__detail"
                  :data-testid="`activity-tab__detail-${groupIndex}-${entryIndex}`"
                >
                  {{ entry.detail }}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import './ActivityTab.scss';

function getInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

function parseDetailBlock(log) {
  const meta = log?.meta || {};
  const text = String(log?.text || '').toLowerCase();

  const comment = meta.comment || meta.message || meta.remark || log.comment;
  if (comment) {
    const quoted = String(comment).trim();
    return quoted.startsWith('"') ? quoted : `"${quoted}"`;
  }

  const fileName = meta.file_name || meta.filename || meta.fileName || meta.attachment;
  if (fileName) {
    return `Attached file: ${fileName}`;
  }

  if (text.includes('comment') && (meta.detail || log.detail)) {
    const detail = String(meta.detail || log.detail).trim();
    return detail.startsWith('"') ? detail : `"${detail}"`;
  }

  if ((text.includes('upload') || text.includes('attached') || text.includes('document')) && log.attachment) {
    return `Attached file: ${log.attachment}`;
  }

  return '';
}

export default {
  name: 'ActivityTab',

  props: {
    task: { type: Object, required: true },
  },

  data() {
    return {
      loading: false,
    };
  },

  computed: {
    loglist() {
      return this.$store.getters.activityLogByResourceId(this.task?._id);
    },

    entries() {
      return this.loglist.map((log) => {
        const user = log.userid_detail || {};
        const userName = user.name || user.contactemail || '';
        const createdAt = log.created_at ? new Date(log.created_at) : null;
        const actionLine = log.text || log.type || 'Activity update';
        const detail = parseDetailBlock(log);

        return {
          dateKey: createdAt
            ? this.$nova.formatDateTime(createdAt, 'YYYY-MM-DD')
            : 'unknown',
          dateLabel: createdAt
            ? this.$nova.formatDateTime(createdAt, 'DD MMM YYYY').toUpperCase()
            : 'UNKNOWN DATE',
          timestamp: createdAt
            ? this.$nova.formatDateTime(createdAt, 'DD MMM YYYY · HH:mm')
            : '—',
          actionLine,
          detail,
          initials: getInitials(userName),
          sortTime: createdAt ? createdAt.getTime() : 0,
        };
      }).sort((a, b) => b.sortTime - a.sortTime);
    },

    groupedEntries() {
      const groups = new Map();

      this.entries.forEach((entry) => {
        if (!groups.has(entry.dateKey)) {
          groups.set(entry.dateKey, {
            dateKey: entry.dateKey,
            dateLabel: entry.dateLabel,
            items: [],
          });
        }
        groups.get(entry.dateKey).items.push(entry);
      });

      return Array.from(groups.values()).sort((a, b) => {
        if (a.dateKey === 'unknown') return 1;
        if (b.dateKey === 'unknown') return -1;
        return b.dateKey.localeCompare(a.dateKey);
      });
    },
  },

  watch: {
    'task._id': {
      immediate: true,
      handler(id) {
        if (id) this.loadActivity();
      },
    },
  },

  methods: {
    async loadActivity() {
      if (!this.task?._id) return;
      this.loading = true;
      try {
        await this.$store.dispatch('fetchActivityLog', {
          resourceId: this.task._id,
          type: [],
          consolidated: false,
        });
      } catch {
        this.$store.commit('setActivityLog', {
          resourceId: this.task._id,
          entries: [],
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
